import { Component, inject, signal, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { PagesModule } from '@gravity-angular/pages';
import { ComponentsModule } from '@gravity-angular/components';
import { BaseModule } from '@gravity-angular/base';
import { HistoricalDataService } from '../../core/services/historical-data.service';
import { DashboardDataService } from '../../core/services/dashboard-data.service';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { EVG_COLORS, PARAMETER_CONFIG, CHART_ANIMATION, CHART_STYLE, REFERENCE_BANDS } from '../../shared/utils/chart-config.constants';
import { ERROR_CODES, WARNING_CODES, ERROR_COLOR_MAP, WARNING_COLOR_MAP } from '../../shared/utils/error-warning-constants';
import { analyzeEVGData } from '../../shared/utils/error-warning-utils';

// Register Chart.js components
Chart.register(...registerables);
Chart.register(zoomPlugin);

interface TooltipEvg {
  id: number;
  color: string;
  voltage: number;
  current: number;
  pwm: number;
  temp: number;
  running: boolean;
  errors: Array<{ code: number; color: string }>;
  warnings: Array<{ code: number; color: string }>;
}

interface ChartTooltipData {
  time: string;
  hasAlarm: boolean;
  alarmEvgs: TooltipEvg[];
  hoveredLabel: string;
  hoveredValue: string;
  hoveredColor: string;
}

@Component({
  selector: 'app-historical-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PagesModule,
    ComponentsModule,
    BaseModule,
    MatProgressBarModule
  ],
  templateUrl: './historical-data.html',
  styleUrl: './historical-data.scss',
})
export class HistoricalData implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('historicalChart', { static: true }) chartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('histTooltipEl', { static: false }) private histTooltipEl?: ElementRef<HTMLDivElement>;

  historicalService = inject(HistoricalDataService);
  dashboardService = inject(DashboardDataService);

  private chart?: Chart;
  private errorAnnotations: Array<{x: number; label: string; color: string}> = [];
  private resizeObserver?: ResizeObserver;

  private readonly annotationPlugin = {
    id: 'errorAnnotationsHist',
    afterDraw: (chart: any) => {
      const anns = this.errorAnnotations;
      if (!anns.length) return;
      const ctx = chart.ctx as CanvasRenderingContext2D;
      const xScale = chart.scales['x'];
      if (!xScale) return;

      // Group by pixel x-position (merge within ±5px), tracking error vs warning
      const byX = new Map<number, { hasError: boolean; hasWarning: boolean }>();
      anns.forEach(ann => {
        const xPx = Math.round(xScale.getPixelForValue(ann.x));
        if (xPx < xScale.left || xPx > xScale.right) return;
        let key = xPx;
        for (const k of byX.keys()) {
          if (Math.abs(k - xPx) <= 5) { key = k; break; }
        }
        const isError = ann.label.startsWith('E');
        if (!byX.has(key)) byX.set(key, { hasError: false, hasWarning: false });
        const entry = byX.get(key)!;
        if (isError) entry.hasError = true;
        else entry.hasWarning = true;
      });

      // Draw small colored tick marks at the bottom edge of the chart area
      const TICK_H = 5;
      const y0 = chart.chartArea.bottom - TICK_H;

      byX.forEach(({ hasError, hasWarning }, xPx) => {
        ctx.save();
        if (hasError && hasWarning) {
          // Split: left half red, right half amber
          ctx.fillStyle = '#ef4444cc';
          ctx.fillRect(xPx - 4, y0, 4, TICK_H);
          ctx.fillStyle = '#f59e0bcc';
          ctx.fillRect(xPx, y0, 4, TICK_H);
        } else if (hasError) {
          ctx.fillStyle = '#ef4444cc';
          ctx.fillRect(xPx - 3, y0, 6, TICK_H);
        } else {
          ctx.fillStyle = '#f59e0bcc';
          ctx.fillRect(xPx - 3, y0, 6, TICK_H);
        }
        ctx.restore();
      });
    }
  };

  private readonly referenceBandPlugin = {
    id: 'referenceBandsHist',
    beforeDatasetsDraw: (chart: any) => {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea) return;
      REFERENCE_BANDS.forEach(({ axisId, lo, hi, color }) => {
        const scale = scales[axisId];
        if (!scale) return;
        const pxHi = Math.min(scale.getPixelForValue(hi), scale.getPixelForValue(lo));
        const pxLo = Math.max(scale.getPixelForValue(hi), scale.getPixelForValue(lo));
        const top  = Math.max(pxHi, chartArea.top);
        const bot  = Math.min(pxLo, chartArea.bottom);
        if (bot <= top) return;
        ctx.save();
        ctx.fillStyle = color;
        ctx.fillRect(chartArea.left, top, chartArea.width, bot - top);
        ctx.restore();
      });
    }
  };

  historicalPoints: Array<{ timestamp: Date; devices: any[] }> = [];
  loadingProgress$ = this.historicalService.loadingProgress$;
  isLoading$ = this.historicalService.isLoading$;
  error$ = this.historicalService.error$;

  // Loading/error state for timeseries POST requests
  isTimeseriesLoading = signal<boolean>(false);
  timeseriesError = signal<string | null>(null);

  // Date range
  fromDate: string = '';
  toDate: string = '';

  // Active preset tracking for visual highlight
  activePreset = signal<'lastHour' | 'last24Hours' | 'last7Days' | null>(null);

  // Custom chart tooltip
  tooltipData = signal<ChartTooltipData | null>(null);
  tooltipLeft = signal<number>(0);
  tooltipTop  = signal<number>(0);

  statsBar = signal<Array<{
    param: string; label: string; suffix: string;
    min: string; max: string; avg: string;
  }>>([]); 

  // Series selection (per-EVG per-param, same pattern as live-data)
  selectedSeries = signal<Array<{evgId: number; parameter: string}>>(
    [1,2,3,4,5,6].flatMap(evgId =>
      ['voltage','current','pwm','temperature'].map(parameter => ({ evgId, parameter }))
    )
  );

  // Error/Warning code signals
  selectedErrorCodes  = signal<number[]>(Object.keys(ERROR_CODES).map(Number));
  selectedWarningCodes = signal<number[]>(Object.keys(WARNING_CODES).map(Number));
  showErrorIndicators  = signal<boolean>(true);
  showWarningIndicators = signal<boolean>(true);
  errorCodesExpanded   = signal<boolean>(false);
  warningCodesExpanded = signal<boolean>(false);

  // Out-of-range notice when API data falls outside the selected date range
  dataOutOfRange = signal<string | null>(null);

  // Expose code maps and Object to template
  ERROR_CODES = ERROR_CODES;
  WARNING_CODES = WARNING_CODES;
  readonly Object = Object;

  ngOnInit(): void {
    // Set default date range (last 24 hours)
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    this.fromDate = this.formatDateForInput(yesterday);
    this.toDate = this.formatDateForInput(now);
  }

  ngAfterViewInit(): void {
    this.initializeChart();

    // Resize chart when sidebar toggles (doesn't fire window.resize)
    if (this.chartCanvas?.nativeElement.parentElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.chart?.resize();
      });
      this.resizeObserver.observe(this.chartCanvas.nativeElement.parentElement);
    }

    // Portal tooltip to document.body to escape xg-sidenav's CSS transform containing block
    if (this.histTooltipEl?.nativeElement) {
      document.body.appendChild(this.histTooltipEl.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.chart) {
      this.chart.destroy();
    }
    this.histTooltipEl?.nativeElement?.remove();
  }

  /**
   * Load historical data using the timeseries POST API
   */
  async loadData(): Promise<void> {
    if (!this.fromDate || !this.toDate) return;

    const from = new Date(this.fromDate);
    const to = new Date(this.toDate);

    this.isTimeseriesLoading.set(true);
    this.timeseriesError.set(null);
    this.dataOutOfRange.set(null);

    try {
      // ISO strings without milliseconds — matches the API format (e.g. 2026-04-02T00:00:00)
      const fromStr = from.toISOString().slice(0, 19);
      const toStr   = to.toISOString().slice(0, 19);

      const points = await this.dashboardService.fetchHistoricalTimeseries(fromStr, toStr);

      if (points.length > 0) {
        this.historicalPoints = points;
      } else {
        this.historicalPoints = [];
        this.timeseriesError.set('No data returned for the selected date range.');
      }

      this.updateChart();
    } catch (error: any) {
      this.timeseriesError.set(error?.message || 'Failed to load historical data');
      console.error('Timeseries load error:', error);
    } finally {
      this.isTimeseriesLoading.set(false);
    }
  }

  /**
   * Set preset date range and load data
   */
  setPreset(preset: string): void {
    this.activePreset.set(preset as 'lastHour' | 'last24Hours' | 'last7Days');

    const now = new Date();
    let from: Date;

    switch (preset) {
      case 'lastHour':
        from = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case 'last24Hours':
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'last7Days':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
    }

    this.fromDate = this.formatDateForInput(from);
    this.toDate = this.formatDateForInput(now);
    this.loadData();
  }

  /**
   * Export visible historical data as CSV
   */
  exportCSV(): void {
    if (!this.historicalPoints.length) return;

    const headers = [
      'timestamp',
      'evg1_voltage', 'evg1_current', 'evg1_temp', 'evg1_pwm',
      'evg2_voltage', 'evg2_current', 'evg2_temp', 'evg2_pwm',
      'evg3_voltage', 'evg3_current', 'evg3_temp', 'evg3_pwm',
      'evg4_voltage', 'evg4_current', 'evg4_temp', 'evg4_pwm',
      'evg5_voltage', 'evg5_current', 'evg5_temp', 'evg5_pwm',
      'evg6_voltage', 'evg6_current', 'evg6_temp', 'evg6_pwm'
    ];

    const rows = this.historicalPoints.map(point => {
      const row: (string | number)[] = [point.timestamp.toISOString()];
      for (let evgId = 1; evgId <= 6; evgId++) {
        if (!point.devices?.length) {
          row.push(0, 0, 0, 0);
          continue;
        }
        const evg = this.dashboardService.extractEVGData(point.devices[0], evgId);
        row.push(
          evg?.voltage ?? 0,
          evg?.current ?? 0,
          evg?.temperature ?? 0,
          evg?.powerLevel ?? 0
        );
      }
      return row.join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evg_historical_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ─── Series Selection ────────────────────────────────────────────────────

  getEVGColor(evgId: number): string {
    return EVG_COLORS[evgId - 1] ?? '#6b7280';
  }

  getSelectedSeriesCount(): number {
    return this.selectedSeries().length;
  }

  isEVGSelected(evgId: number): 'all' | 'partial' | 'none' {
    const params = ['voltage', 'current', 'pwm', 'temperature'];
    const count = params.filter(p =>
      this.selectedSeries().some(s => s.evgId === evgId && s.parameter === p)
    ).length;
    if (count === 4) return 'all';
    if (count > 0) return 'partial';
    return 'none';
  }

  toggleEVG(evgId: number): void {
    const params = ['voltage', 'current', 'pwm', 'temperature'] as const;
    const cur = this.selectedSeries();
    const allSel = params.every(p => cur.some(s => s.evgId === evgId && s.parameter === p));
    const without = cur.filter(s => s.evgId !== evgId);
    this.selectedSeries.set(
      allSel ? without : [...without, ...params.map(p => ({ evgId, parameter: p }))]
    );
    this.updateChart();
  }

  isParameterSelected(evgId: number, param: string): boolean {
    return this.selectedSeries().some(s => s.evgId === evgId && s.parameter === param);
  }

  toggleParameter(evgId: number, parameter: string): void {
    const cur = this.selectedSeries();
    const exists = cur.find(s => s.evgId === evgId && s.parameter === parameter);
    this.selectedSeries.set(
      exists
        ? cur.filter(s => !(s.evgId === evgId && s.parameter === parameter))
        : [...cur, { evgId, parameter }]
    );
    this.updateChart();
  }

  deselectAllSeries(): void {
    this.selectedSeries.set([]);
    this.updateChart();
  }

  selectAllSeries(): void {
    this.selectedSeries.set(
      [1,2,3,4,5,6].flatMap(evgId =>
        ['voltage','current','pwm','temperature'].map(parameter => ({ evgId, parameter }))
      )
    );
    this.updateChart();
  }

  selectByParameter(param: 'voltage' | 'current' | 'pwm' | 'temperature'): void {
    this.selectedSeries.set(
      [1, 2, 3, 4, 5, 6].map(evgId => ({ evgId, parameter: param }))
    );
    this.updateChart();
  }

  toggleShowErrors(): void {
    const next = !this.showErrorIndicators();
    this.showErrorIndicators.set(next);
    if (!next) this.selectedErrorCodes.set([]);
    else this.selectedErrorCodes.set(Object.keys(ERROR_CODES).map(Number));
    this.updateChart();
  }

  toggleShowWarnings(): void {
    const next = !this.showWarningIndicators();
    this.showWarningIndicators.set(next);
    if (!next) this.selectedWarningCodes.set([]);
    else this.selectedWarningCodes.set(Object.keys(WARNING_CODES).map(Number));
    this.updateChart();
  }

  // ─── Error/Warning Code Toggles ─────────────────────────────────────────

  toggleErrorCode(code: number): void {
    const cur = this.selectedErrorCodes();
    this.selectedErrorCodes.set(cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code]);
    this.updateChart();
  }

  toggleWarningCode(code: number): void {
    const cur = this.selectedWarningCodes();
    this.selectedWarningCodes.set(cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code]);
    this.updateChart();
  }

  selectAllErrors(): void {
    this.selectedErrorCodes.set(Object.keys(ERROR_CODES).map(Number));
    this.updateChart();
  }

  deselectAllErrors(): void {
    this.selectedErrorCodes.set([]);
    this.updateChart();
  }

  selectAllWarnings(): void {
    this.selectedWarningCodes.set(Object.keys(WARNING_CODES).map(Number));
    this.updateChart();
  }

  deselectAllWarnings(): void {
    this.selectedWarningCodes.set([]);
    this.updateChart();
  }

  getErrorColor(code: number): string {
    return ERROR_COLOR_MAP[code] || '#ef4444';
  }

  getWarningColor(code: number): string {
    return WARNING_COLOR_MAP[code] || '#f59e0b';
  }

  getErrorDescription(code: number): string {
    const descriptions: Record<number, string> = {
      120: 'Both lamps off after ignition cycles.',
      130: 'Communication error with LMS BUS.',
      140: 'UZK voltage too low.',
      150: 'UZK voltage too high.',
      160: 'EVG temperature exceeds safe operating limits.',
      170: 'Power supply failure detected.',
      180: 'Lamp current overflow.',
      190: 'Critical lamp current levels.'
    };
    return descriptions[code] || 'No description available.';
  }

  getWarningDescription(code: number): string {
    const descriptions: Record<number, string> = {
      210: 'UZK voltage dropping.',
      220: 'Power supply voltage is low.',
      230: 'Lamp re-ignition cycle triggered.',
      240: 'Lamp 1 showing degraded performance.',
      250: 'Lamp 2 showing degraded performance.'
    };
    return descriptions[code] || 'No description available.';
  }

  // ─── Chart ────────────────────────────────────────────────────────────────

  /**
   * Initialize Chart.js chart with ms-timestamp linear x-axis
   */
  private initializeChart(): void {
    if (!this.chartCanvas) return;

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    const config: ChartConfiguration = {
      type: 'line',
      data: { datasets: [] },
      plugins: [this.annotationPlugin, this.referenceBandPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_ANIMATION,
        plugins: {
          legend: { display: true, position: 'top' },
          title: { display: false },
          zoom: {
            zoom: {
              wheel: { enabled: true, speed: 0.1 },
              pinch: { enabled: true },
              mode: 'x' as const,
              onZoom: () => this.updateStatsBar()
            },
            pan: {
              enabled: true,
              mode: 'x' as const,
              onPan: () => this.updateStatsBar()
            },
            limits: { x: { minRange: 60_000 } }
          },
          tooltip: {
            enabled: false,
            external: (context: any) => {
              const { chart, tooltip } = context;
              if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
                this.tooltipData.set(null);
                return;
              }
              if (!this.historicalPoints.length) return;

              const hoverX: number = tooltip.dataPoints[0].parsed.x;
              const nearest = this.historicalPoints.reduce((best, cur) =>
                Math.abs(cur.timestamp.getTime() - hoverX) < Math.abs(best.timestamp.getTime() - hoverX) ? cur : best
              );

              const time = nearest.timestamp.toLocaleString('en-GB', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
              }).replace(',', '');

              // Build per-EVG data for alarm tooltip — only selected EVGs
              const alarmEvgs: TooltipEvg[] = [];
              const activeEvgIds = [...new Set(this.selectedSeries().map(s => s.evgId))];
              for (const i of activeEvgIds) {
                if (!nearest.devices?.length) continue;
                const evgData = this.dashboardService.extractEVGData(nearest.devices[0], i);
                if (!evgData) continue;
                const { errors, warnings } = analyzeEVGData(
                  { ...evgData, lamp1_status: evgData.running, lamp2_status: evgData.running }, i
                );
                const filteredErrors   = errors.filter(c => this.selectedErrorCodes().includes(c));
                const filteredWarnings = warnings.filter(c => this.selectedWarningCodes().includes(c));
                if (filteredErrors.length > 0 || filteredWarnings.length > 0) {
                  alarmEvgs.push({
                    id: i,
                    color: EVG_COLORS[i - 1],
                    voltage: evgData.voltage,
                    current: evgData.current,
                    pwm: evgData.powerLevel,
                    temp: evgData.temperature,
                    running: evgData.running,
                    errors:   filteredErrors.map(c => ({ code: c, color: ERROR_COLOR_MAP[c]   ?? '#ef4444' })),
                    warnings: filteredWarnings.map(c => ({ code: c, color: WARNING_COLOR_MAP[c] ?? '#f59e0b' }))
                  });
                }
              }

              const hasAlarm = alarmEvgs.length > 0;
              const dp = tooltip.dataPoints[0];
              const hoveredLabel = dp.dataset?.label ?? '';
              const hoveredValue = dp.formattedValue ?? '';
              const hoveredColor = dp.dataset?.borderColor ?? '#6b7280';

              // ── Smart tooltip positioning using fixed (viewport) coords ──
              // position: fixed bypasses all parent overflow:hidden clipping
              const canvasRect = chart.canvas.getBoundingClientRect();
              const GAP = 14;
              const TIP_W = hasAlarm ? 540 : 280;
              const TIP_H = hasAlarm ? Math.min(80 + alarmEvgs.length * 130, 420) : 110;

              // Cursor in viewport coordinates
              const cursorX = canvasRect.left + tooltip.caretX;
              const cursorY = canvasRect.top  + tooltip.caretY;
              const VW = window.innerWidth;
              const VH = window.innerHeight;

              // Horizontal: prefer right; flip left if near viewport edge
              let left = cursorX + GAP;
              if (left + TIP_W > VW - 8) left = cursorX - TIP_W - GAP;
              left = Math.max(8, Math.min(left, VW - TIP_W - 8));

              // Vertical: prefer above cursor; fall back to below if near top
              let top = cursorY - TIP_H - GAP;
              if (top < 8) top = cursorY + GAP;
              top = Math.max(8, Math.min(top, VH - TIP_H - 8));

              this.tooltipLeft.set(left);
              this.tooltipTop.set(top);
              this.tooltipData.set({ time, hasAlarm, alarmEvgs, hoveredLabel, hoveredValue, hoveredColor });
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            display: true,
            title: { display: true, text: 'Time' },
            ticks: {
              callback: (val: any) => this.formatChartTick(val)
            }
          },
          y: { beginAtZero: false }
        }
      }
    };

    this.chart = new Chart(ctx, config);
    this.updateChart();
  }

  /**
   * Update chart with historical data and error/warning annotations
   */
  updateChart(): void {
    if (!this.chart) return;

    if (this.historicalPoints.length === 0) {
      this.errorAnnotations = [];
      this.chart.data.datasets = [];
      this.chart.update();
      return;
    }

    // --- Compute annotations ---
    const newAnnotations: Array<{x: number; label: string; color: string}> = [];
    if (this.showErrorIndicators() || this.showWarningIndicators()) {
      this.historicalPoints.forEach(h => {
        if (!h.devices?.length) return;
        const ts = h.timestamp.getTime();
        for (let i = 1; i <= 6; i++) {
          const evg = this.dashboardService.extractEVGData(h.devices[0], i);
          if (!evg) continue;
          const { errors, warnings } = analyzeEVGData(
            { ...evg, lamp1_status: evg.running, lamp2_status: evg.running }, i
          );
          if (this.showErrorIndicators()) {
            errors.filter(c => this.selectedErrorCodes().includes(c)).forEach(c => {
              newAnnotations.push({ x: ts, label: `E${c}·${i}`, color: ERROR_COLOR_MAP[c] ?? '#ef4444' });
            });
          }
          if (this.showWarningIndicators()) {
            warnings.filter(c => this.selectedWarningCodes().includes(c)).forEach(c => {
              newAnnotations.push({ x: ts, label: `W${c}·${i}`, color: WARNING_COLOR_MAP[c] ?? '#f59e0b' });
            });
          }
        }
      });
    }
    this.errorAnnotations = newAnnotations;

    // --- Build datasets ---
    const series = this.selectedSeries();
    if (series.length === 0) {
      this.chart.data.datasets = [];
      this.chart.update('none');
      return;
    }

    const paramMap: Record<string, string> = {
      voltage: 'voltage', current: 'current', pwm: 'powerLevel', temperature: 'temperature'
    };

    const datasets = series.map(({ evgId, parameter }) => {
      const color = EVG_COLORS[evgId - 1];
      const cfg = PARAMETER_CONFIG[parameter as keyof typeof PARAMETER_CONFIG];
      const data = this.historicalPoints
        .filter(h => h.devices?.length)
        .map(h => {
          const evgData = this.dashboardService.extractEVGData(h.devices[0], evgId);
          if (!evgData) return null;
          return { x: h.timestamp.getTime(), y: (evgData as any)[paramMap[parameter]] };
        })
        .filter(Boolean);
      return {
        label: `EVG ${evgId} - ${cfg.label} (${cfg.suffix})`,
        data,
        borderColor: color,
        backgroundColor: (context: any) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return color + '10';
          const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, color + '30');
          g.addColorStop(1, color + '00');
          return g;
        },
        yAxisID: cfg.yAxisID,
        fill: true,
        ...CHART_STYLE
      };
    });

    // --- Build y-axes ---
    const usedParams = new Set(series.map(s => s.parameter));
    const yAxes: any = {};
    if (usedParams.has('voltage')) {
      yAxes['y-voltage'] = {
        type: 'linear', display: true, position: 'left',
        title: { display: true, text: `${PARAMETER_CONFIG.voltage.label} (${PARAMETER_CONFIG.voltage.suffix})` },
        grid: { drawOnChartArea: true, color: PARAMETER_CONFIG.voltage.gridColor }
      };
    }
    if (usedParams.has('current')) {
      yAxes['y-current'] = {
        type: 'linear', display: true, position: 'right',
        title: { display: true, text: `${PARAMETER_CONFIG.current.label} (${PARAMETER_CONFIG.current.suffix})` },
        grid: { drawOnChartArea: false }
      };
    }
    if (usedParams.has('pwm')) {
      yAxes['y-pwm'] = {
        type: 'linear', display: true, position: 'right',
        title: { display: true, text: `${PARAMETER_CONFIG.pwm.label} (${PARAMETER_CONFIG.pwm.suffix})` },
        grid: { drawOnChartArea: false }, suggestedMin: 0
      };
    }
    if (usedParams.has('temperature')) {
      yAxes['y-temp'] = {
        type: 'linear', display: true, position: 'right',
        title: { display: true, text: `${PARAMETER_CONFIG.temperature.label} (${PARAMETER_CONFIG.temperature.suffix})` },
        grid: { drawOnChartArea: false }
      };
    }

    this.chart.data.datasets = datasets;
    this.chart.options.scales = {
      x: {
        type: 'linear', display: true,
        title: { display: true, text: 'Time' },
        ticks: {
          callback: (val: any) => new Date(val).toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
          })
        }
      },
      ...yAxes
    };
    this.chart.update('none');
    // Force re-measure after data load so the full-width canvas fills its container
    requestAnimationFrame(() => this.chart?.resize());
    this.updateStatsBar();
  }

  resetZoom(): void {
    (this.chart as any)?.resetZoom();
    this.updateStatsBar();
  }

  private updateStatsBar(): void {
    if (!this.chart?.data.datasets.length) { this.statsBar.set([]); return; }
    const scale = this.chart.scales['x'];
    const xMin = scale?.min ?? -Infinity;
    const xMax = scale?.max ?? Infinity;
    type Acc = { min: number; max: number; sum: number; count: number };
    const acc: Record<string, Acc> = {};
    this.chart.data.datasets.forEach((ds: any) => {
      const param = Object.keys(PARAMETER_CONFIG).find(
        k => k !== 'temperature' && (PARAMETER_CONFIG as any)[k].yAxisID === ds.yAxisID
      );
      if (!param) return;
      if (!acc[param]) acc[param] = { min: Infinity, max: -Infinity, sum: 0, count: 0 };
      const a = acc[param];
      (ds.data as {x: number; y: number}[]).forEach(pt => {
        if (pt.x < xMin || pt.x > xMax || pt.y == null) return;
        a.min = Math.min(a.min, pt.y); a.max = Math.max(a.max, pt.y);
        a.sum += pt.y; a.count++;
      });
    });
    this.statsBar.set(
      Object.entries(acc).filter(([, a]) => a.count > 0).map(([param, a]) => {
        const cfg = (PARAMETER_CONFIG as any)[param];
        return { param, label: cfg.label, suffix: cfg.suffix,
                 min: a.min.toFixed(1), max: a.max.toFixed(1), avg: (a.sum / a.count).toFixed(1) };
      })
    );
  }

  /**
   * Smart x-axis tick: shows date+time for multi-day ranges, time-only for same-day
   */
  private formatChartTick(val: any): string {
    const d = new Date(val);
    const rangeMs = this.historicalPoints.length >= 2
      ? this.historicalPoints.at(-1)!.timestamp.getTime() - this.historicalPoints[0].timestamp.getTime()
      : 0;
    if (rangeMs > 23 * 60 * 60 * 1000) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' ' +
             d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }

  /**
   * Returns null for invalid Date objects so the date pipe doesn't throw
   */
  safeDate(d: Date | undefined): Date | null {
    if (!d) return null;
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Format date for datetime-local input
   */
  private formatDateForInput(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
